import { useEffect, useMemo, useState } from 'react';
import { useSaldosClientes } from './useSaldosClientes';
import {
  subscribeProximosVencimientos,
  subscribeProximosVencimientosMeta,
  subscribeVencimientosFinanciacion,
} from '../services/proximosVencimientosService';
import { acotarAComitentes, eventosDeFinanciacion, eventosDesdeSgc } from '../utils/proximosVencimientos';
import { vencimientosFinanciacionEnVivo } from '../utils/vencimientosFinanciacion';

const CARGANDO = { docs: true, meta: true, archivados: true };

/**
 * Próximos vencimientos: la proyección del SGC más las cauciones y pases tomadores.
 * Las del día salen del proveedor de saldos, que ya las tiene en vivo.
 *
 * Alcance: en modo productor, siempre su padrón (las reglas lo exigen). En el staff,
 * `alcance` (las cuentas de un comercial) o null para toda la casa.
 *
 * `meta` en null con `isLoading` false significa que no hay proyección del SGC: la
 * UI tiene que decirlo y no mostrar "sin vencimientos".
 */
export const useProximosVencimientos = (alcance = null) => {
  const { comitentesProductor, allCauciones, allPases, fechaHoy } = useSaldosClientes();
  const [docs, setDocs] = useState([]);
  const [archivados, setArchivados] = useState([]);
  const [meta, setMeta] = useState(null);
  const [cargando, setCargando] = useState(CARGANDO);
  const [error, setError] = useState(null);

  const comitentesScope = Array.isArray(comitentesProductor) ? comitentesProductor : alcance;
  const comitentesKey = Array.isArray(comitentesScope) ? [...comitentesScope].map(String).sort().join('|') : null;
  const comitentes = useMemo(
    () => (comitentesKey === null ? null : (comitentesKey ? comitentesKey.split('|') : [])),
    [comitentesKey],
  );

  useEffect(() => {
    setCargando(CARGANDO);
    setError(null);
    const listo = (clave) => setCargando(prev => ({ ...prev, [clave]: false }));
    const fallo = (clave) => (err) => {
      setError(err);
      listo(clave);
    };
    const unsubs = [
      subscribeProximosVencimientos(comitentes, (data) => {
        setDocs(data);
        listo('docs');
      }, fallo('docs')),
      subscribeProximosVencimientosMeta((data) => {
        setMeta(data);
        listo('meta');
      }, fallo('meta')),
      subscribeVencimientosFinanciacion(comitentes, (data) => {
        setArchivados(data);
        listo('archivados');
      }, fallo('archivados')),
    ];
    return () => unsubs.forEach(unsub => unsub());
  }, [comitentes]);

  const eventos = useMemo(() => acotarAComitentes([
    ...eventosDesdeSgc(docs),
    ...eventosDeFinanciacion([...archivados, ...vencimientosFinanciacionEnVivo(allCauciones, allPases)]),
  ], comitentes), [docs, archivados, allCauciones, allPases, comitentes]);

  const eventosOmitidos = useMemo(
    () => docs.reduce((acc, d) => acc + (Number(d.eventosOmitidos) || 0), 0),
    [docs],
  );

  return {
    eventos,
    meta,
    fechaHoy,
    acotado: comitentes !== null,
    eventosOmitidos,
    isLoading: cargando.docs || cargando.meta || cargando.archivados,
    error,
  };
};
